import { useState, useRef, useEffect } from 'react';
import { Box, Checkbox, FormControlLabel, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { MANUAL_PREMIUM_PLAN_ID } from './ManualPremiumFormDialog';
import { INSURERS } from '../../utils/insurers';
import { isBoldCell } from '../../utils/tobTableEditing';
import TOBTableMenuBar from './TOBTableMenuBar';
import { DEFAULT_TOB_LANGUAGE } from '../../services/TOBService';

const HEADER_BG = '#1a3055';
const HEADER_COLOR = '#ffffff';
const CELL_BORDER = '1px solid #d0d7e2';
const CELL_PAD = '7px 12px';
const FIRST_COLUMN_WIDTH = 320;

const SELECTED_CELL_OUTLINE = 'inset 0 0 0 2px #673ab7';
const ADD_COLUMN_COLOR = '#1976d2';
// Same yellow as the stale icon on the plan chips.
const MISSING_PREMIUM_COLOR = '#fbc02d';
// Hover area just past the table's right edge, inside the container's padding.
const ADD_COLUMN_ZONE_WIDTH = 16;

// Columns for "Leave Blank" quotes render as plain white space with no borders
const BLANK_CELL_STYLE = { background: '#fff', color: 'transparent', border: 'none' };

const MAX_PLANS_FOR_LARGE_FONT = 3;
const LARGE_FONT_SIZE = 13;
const REGULAR_FONT_SIZE = 11;

const DEDUCTIBLE_NORMALIZATION = {
    'zero deductible': 'Nil Deductible',
    'standard': 'Nil Deductible',
    '0 deductible': 'Nil Deductible',
    'nil deductible': 'Nil Deductible',
    'nil': 'Nil Deductible',
};

const CURRENCY_SYMBOLS = {
    '$': 'USD',
    'us$': 'USD',
    's$': 'SGD',
    '€': 'EUR',
    '£': 'GBP',
};

const SUPPORTED_CURRENCY_CODES = new Set(['USD', 'SGD', 'GBP', 'EUR']);

function formatCurrencyDeductible(value) {
    const normalized = value.trim();

    const currencyCodeMatch = normalized.match(/^([A-Za-z]{3})\s*([\d,]+)(.*)$/i);
    if (currencyCodeMatch) {
        const currency = currencyCodeMatch[1].toUpperCase();
        if (!SUPPORTED_CURRENCY_CODES.has(currency)) {
            return value;
        }
        const amount = currencyCodeMatch[2].replace(/,/g, '');
        const suffix = currencyCodeMatch[3].trim();
        return `${currency} ${amount}${suffix ? ` ${suffix}` : ' Deductible'}`.trim();
    }

    const symbolMatch = normalized.match(/^([\$€£]|us\$|s\$)\s*([\d,]+)(.*)$/i);
    if (symbolMatch) {
        const symbol = symbolMatch[1].toLowerCase();
        const currency = CURRENCY_SYMBOLS[symbol];
        if (!currency) {
            return value;
        }
        const amount = symbolMatch[2].replace(/,/g, '');
        const suffix = symbolMatch[3].trim();
        return `${currency} ${amount}${suffix ? ` ${suffix}` : ' Deductible'}`.trim();
    }

    return value;
}

// Bahasa Indonesia wording for the normalized deductible text, applied to each " | " part
// (e.g. "Nil Deductible | OP Co-Pay: Nil"). Anything not matched stays in English.
const BAHASA_DEDUCTIBLE_REPLACEMENTS = [
    [/^nil deductible$/i, 'Tanpa Risiko Sendiri'],
    // "USD 800 Deductible" -> "Risiko Sendiri USD 800"
    [/\b([A-Z]{3} [\d,.]+) Deductible\b/g, 'Risiko Sendiri $1'],
    [/^OP Co-Pay:/i, 'Co-Pay Rawat Jalan:'],
    [/^Co-ins:/i, 'Ko-asuransi:'],
    [/:\s*Nil$/i, ': Tidak Ada'],
];

function translateDeductiblePart(part, language) {
    if (language !== 'Bahasa Indonesia') return part;
    return BAHASA_DEDUCTIBLE_REPLACEMENTS.reduce((text, [pattern, replacement]) => text.replace(pattern, replacement), part);
}

function normalizeDeductibleDisplay(rawDeductible, language = DEFAULT_TOB_LANGUAGE) {
    if (!rawDeductible || typeof rawDeductible !== 'string') {
        return rawDeductible;
    }

    const parts = rawDeductible.split('|').map((part) => part.trim());
    const firstPart = parts[0];
    const lowerFirst = firstPart.toLowerCase();

    let normalizedFirst = DEDUCTIBLE_NORMALIZATION[lowerFirst];
    if (!normalizedFirst) {
        normalizedFirst = formatCurrencyDeductible(firstPart) || firstPart;
    }

    return [normalizedFirst, ...parts.slice(1)].map(part => translateDeductiblePart(part, language)).join(' | ');
}

const INSURER_DISPLAY_MAP = INSURERS.reduce((map, insurer) => {
    const displayLabel = insurer.standardLabel || insurer.label;
    map[insurer.key.toLowerCase()] = displayLabel;
    map[insurer.label.toLowerCase()] = displayLabel;
    return map;
}, {});

function normalizeInsurerName(rawInsurer) {
    if (!rawInsurer || typeof rawInsurer !== 'string') {
        return rawInsurer;
    }

    const normalized = rawInsurer.trim();
    const lower = normalized.toLowerCase();
    if (INSURER_DISPLAY_MAP[lower]) {
        return INSURER_DISPLAY_MAP[lower];
    }

    const found = INSURERS.find((insurer) => {
        return lower === insurer.key.toLowerCase()
            || lower === insurer.label.toLowerCase()
            || lower.includes(insurer.key.toLowerCase())
            || lower.includes(insurer.label.toLowerCase());
    });

    return found ? (found.standardLabel || found.label) : normalized;
}

// Bahasa Indonesia for the areas of cover the plan dialogs offer, keyed by the normalized
// English display text in lower case. Anything not listed stays in English.
const BAHASA_AREA_OF_COVER = {
    'worldwide': 'Seluruh Dunia',
    'worldwide excluding usa': 'Seluruh Dunia kecuali Amerika Serikat',
    'worldwide excl. u.s.a.': 'Seluruh Dunia kecuali Amerika Serikat',
    'worldwide incl usa': 'Seluruh Dunia termasuk Amerika Serikat',
    'worldwide including usa': 'Seluruh Dunia termasuk Amerika Serikat',
    'excl usa': 'Kecuali Amerika Serikat',
    'asia only': 'Hanya Asia',
    'asia pacific': 'Asia Pasifik',
    'africa only': 'Hanya Afrika',
    'africa & the indian subcontinent': 'Afrika & Anak Benua India',
    'singapore only': 'Hanya Singapura',
    'philippines': 'Filipina',
    'indonesia': 'Indonesia',
    'european economic area': 'Kawasan Ekonomi Eropa',
    'rest of the world': 'Seluruh Dunia Lainnya',
    'rest of the world (excl. hong kong & u.s. & european economic area)': 'Seluruh Dunia Lainnya (kecuali Hong Kong, Amerika Serikat & Kawasan Ekonomi Eropa)',
};

function translateAreaOfCover(area) {
    const translated = BAHASA_AREA_OF_COVER[area.trim().toLowerCase()];
    if (translated) return translated;

    // e.g. "Worldwide excluding USA 90 days" -> "Seluruh Dunia kecuali Amerika Serikat (90 hari)"
    const daysMatch = area.match(/^(.*?)\s+(\d+)\s*days$/i);
    const translatedBase = daysMatch && BAHASA_AREA_OF_COVER[daysMatch[1].trim().toLowerCase()];
    return translatedBase ? `${translatedBase} (${daysMatch[2]} hari)` : area;
}

export function normalizeAreaOfCoverDisplay(rawArea, language = DEFAULT_TOB_LANGUAGE) {
    if (!rawArea || typeof rawArea !== 'string') {
        return rawArea;
    }

    const display = rawArea.replace(/worldwide\s*excl\s*usa/i, 'Worldwide excluding USA');
    return language === 'Bahasa Indonesia' ? translateAreaOfCover(display) : display;
}

// The table's own labels per TOB language (see TOB_LANGUAGES). Plan Type / Area of cover use
// the same wording as the Bahasa Indonesia TOB spreadsheet.
const LABELS = {
    'English': {
        premiumComparison: 'Premium Comparison',
        planType: 'Plan Type',
        deductible: 'Deductible',
        areaOfCover: 'Area of cover',
        indicativePremium: 'Indicative Premium',
        indicativeFrequencyPremium: frequency => `Indicative ${frequency} Premium`,
        ourIndicativeQuotation: 'OUR INDICATIVE QUOTATION',
        recommended: 'RECOMMENDED',
        disclaimer: '*** Kindly note that the premiums quoted are estimated figures and may vary from the final premium issued on the invoice. ***',
        frequencies: {
            'annually': 'Annual',
            'semi annually': 'Semi Annual',
            'quarterly': 'Quarterly',
            'monthly': 'Monthly',
        },
    },
    'Bahasa Indonesia': {
        premiumComparison: 'Perbandingan Premi',
        planType: 'Jenis Paket',
        deductible: 'Risiko Sendiri',
        areaOfCover: 'Wilayah Pertanggungan',
        indicativePremium: 'Indikasi Premi',
        indicativeFrequencyPremium: frequency => `Indikasi Premi ${frequency}`,
        ourIndicativeQuotation: 'INDIKASI PENAWARAN KAMI',
        recommended: 'DIREKOMENDASIKAN',
        disclaimer: '*** Mohon diperhatikan bahwa premi yang tercantum merupakan angka perkiraan dan dapat berbeda dari premi final yang tertera pada tagihan. ***',
        frequencies: {
            'annually': 'Tahunan',
            'semi annually': 'Semesteran',
            'quarterly': 'Triwulanan',
            'monthly': 'Bulanan',
        },
    },
};

function normalizeFrequencyLabel(rawFrequency, labels = LABELS[DEFAULT_TOB_LANGUAGE]) {
    if (!rawFrequency || typeof rawFrequency !== 'string') {
        return '';
    }

    const trimmed = rawFrequency.trim();
    return labels.frequencies[trimmed.toLowerCase()] || trimmed;
}

// Every editable cell is addressed by its row and the plan's index in the saved quotation.
// The shared rows hold one value per plan, kept the same across every client's premium; a
// client's rate row (`rate:<client uid>`) belongs to that client's premium alone.
const SHARED_ROW_FIELDS = ['insurer', 'plan_label', 'included_benefits', 'deductible', 'area_of_coverage'];
const RATE_ROW_PREFIX = 'rate:';

// How each row renders before any formatting, so Bold toggles from what the user sees.
const DEFAULT_FONT_WEIGHT = {
    insurer: '700',
    plan_label: '700',
    included_benefits: '400',
    deductible: '700',
    area_of_coverage: '700',
    premium_rate: '400',
};

const cellKey = (rowKey, planIdx) => `${rowKey}|${planIdx}`;

function parseCellKey(key) {
    const separator = key.lastIndexOf('|');
    return { rowKey: key.slice(0, separator), planIdx: Number(key.slice(separator + 1)) };
}

const rowField = rowKey => (rowKey.startsWith(RATE_ROW_PREFIX) ? 'premium_rate' : rowKey);
const rowClientUid = rowKey => (rowKey.startsWith(RATE_ROW_PREFIX) ? rowKey.slice(RATE_ROW_PREFIX.length) : null);

// What the parent's update handlers receive: clientUid is null for the shared rows.
function toCellTarget(key) {
    const { rowKey, planIdx } = parseCellKey(key);
    return { field: rowField(rowKey), clientUid: rowClientUid(rowKey), planIdx };
}

// Typed rates may carry thousands separators or a currency; an empty cell clears the rate.
// Returns undefined for text that is not a number, so the edit is dropped.
function parseRateInput(text) {
    const cleaned = String(text).replace(/[^\d.-]/g, '');
    if (cleaned === '') return null;
    const rate = Number(cleaned);
    return Number.isFinite(rate) ? rate : undefined;
}

// Display order: "Leave Blank" columns move to the right so filled plans sit together on the
// left. columnOrder[displayIdx] is the plan's index in the saved quotation.
function getQuotationLayout(quotation) {
    const savedFirstPremiums = quotation.client_premiums[0]?.premiums || [];
    const columnOrder = savedFirstPremiums.map((_, i) => i)
        .sort((a, b) => Number(!!savedFirstPremiums[a].blank) - Number(!!savedFirstPremiums[b].blank));
    return {
        columnOrder,
        // Blank columns have nothing to edit, so selection and Tab step over them.
        editableColumns: columnOrder.filter(i => !savedFirstPremiums[i].blank),
        rowKeys: [...SHARED_ROW_FIELDS, ...quotation.client_premiums.map(cp => RATE_ROW_PREFIX + cp.client_uid)],
    };
}

function getCellPremium(quotation, key) {
    const { rowKey, planIdx } = parseCellKey(key);
    const clientUid = rowClientUid(rowKey);
    const clientPremium = clientUid
        ? quotation.client_premiums.find(cp => cp.client_uid === clientUid)
        : quotation.client_premiums[0];
    return clientPremium?.premiums?.[planIdx];
}

function getCellEditValue(quotation, key) {
    const premium = getCellPremium(quotation, key);
    if (!premium) return '';
    const field = rowField(parseCellKey(key).rowKey);
    if (field === 'premium_rate') return premium.premium_rate != null ? String(premium.premium_rate) : '';
    // The insurer is stored as its key (e.g. "lifeline"), so the editor starts from the name shown.
    if (field === 'insurer') return normalizeInsurerName(premium.insurer) || '';
    return premium[field] || '';
}

const joinClasses = (...classes) => classes.filter(Boolean).join(' ') || undefined;

export default function QuotationPreviewTable({ quotations, clients, onUpdateCell, onFormatCells, onUpdateRecommended, onAddManualPremium, stalePlanKeys, onOpenPlan, language = DEFAULT_TOB_LANGUAGE }) {
    const labels = LABELS[language] || LABELS[DEFAULT_TOB_LANGUAGE];
    const [editingCell, setEditingCell] = useState(null); // { quotationUid, key, initialValue, initialCurrency }
    const [editValue, setEditValue] = useState('');
    // A rate cell shows its currency beside the amount, so its editor has an input for each.
    const [editCurrency, setEditCurrency] = useState('');
    // { quotationUid, keys: ['deductible|0', 'rate:<client uid>|1', ...] } -- always within one quotation.
    const [selection, setSelection] = useState(null);
    // Where a Shift-click measures its range from.
    const [anchorKey, setAnchorKey] = useState(null);
    const [hoveredPremiumIndexByQuotation, setHoveredPremiumIndexByQuotation] = useState({});
    // The formatting bar only shows while the preview is hovered and has a selection.
    const [tableHovered, setTableHovered] = useState(false);
    const inputRef = useRef();
    const currencyInputRef = useRef();
    // Wraps a rate cell's two inputs, so moving focus between them doesn't count as leaving the cell.
    const rateEditorRef = useRef();
    // Tabbing tears down the current editor to open the next one, and the resulting blur
    // would commit over the cell we just moved into and close it again.
    const suppressBlurRef = useRef(false);
    // A Manual Premium column added from the "+" lands in quotations on a later render;
    // { quotationUid, planUid } holds where to start typing once it is there.
    const [pendingManualEdit, setPendingManualEdit] = useState(null);

    useEffect(() => {
        if (!pendingManualEdit) return;
        const quotation = quotations?.find(q => q.uid === pendingManualEdit.quotationUid);
        const planIdx = quotation?.client_premiums?.[0]?.premiums?.findIndex(p => p.plan_uid === pendingManualEdit.planUid) ?? -1;
        if (planIdx === -1) return;

        setPendingManualEdit(null);
        const key = cellKey('insurer', planIdx);
        setAnchorKey(key);
        setSelection({ quotationUid: quotation.uid, keys: [key] });
        startEdit(quotation, key);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quotations, pendingManualEdit]);

    if (!quotations || quotations.length === 0) {
        return (
            <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                No quotation data. Add quotes and plans to see the preview.
            </Typography>
        );
    }

    const hasAnyPlan = quotations.some(q => q.client_premiums?.[0]?.premiums?.length > 0);
    if (!hasAnyPlan) {
        return (
            <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                Add plans to view the premium comparison.
            </Typography>
        );
    }

    function getFirstClientPremiumsLength() {
        return quotations[0]?.client_premiums?.[0]?.premiums?.length || 0;
    }

    const findQuotation = quotationUid => quotations.find(q => q.uid === quotationUid);

    function startEdit(quotation, key) {
        const initialValue = getCellEditValue(quotation, key);
        const initialCurrency = getCellPremium(quotation, key)?.currency || '';
        setEditingCell({ quotationUid: quotation.uid, key, initialValue, initialCurrency });
        setEditValue(initialValue);
        setEditCurrency(initialCurrency);
        setTimeout(() => inputRef.current?.focus(), 0);
    }

    // Leaving a cell untouched must not count as an edit, or merely clicking through the
    // table would flag premiums as manually edited.
    function commitEdit() {
        if (editingCell) {
            const target = toCellTarget(editingCell.key);

            if (editValue !== editingCell.initialValue) {
                const value = target.field === 'premium_rate' ? parseRateInput(editValue) : editValue;
                if (value !== undefined) onUpdateCell?.(editingCell.quotationUid, target, value);
            }

            // One plan is quoted in one currency -- the total row shows a single one -- so a
            // currency typed into any client's cell applies to the whole plan.
            if (target.field === 'premium_rate' && editCurrency.trim() !== editingCell.initialCurrency) {
                onUpdateCell?.(editingCell.quotationUid, { field: 'currency', clientUid: null, planIdx: target.planIdx }, editCurrency.trim());
            }
        }
        setEditingCell(null);
    }

    // Clicking a formatting button blurs the editor and closes it, so the cells the menu bar
    // acts on are tracked separately from the one being typed into. Shift/Ctrl-clicking in
    // another quotation starts a new selection there.
    function handleCellClick(event, quotation, key, isEditingThisCell) {
        const sameQuotation = selection?.quotationUid === quotation.uid;

        if (event.shiftKey && sameQuotation && anchorKey) {
            const { rowKeys, editableColumns } = getQuotationLayout(quotation);
            const from = parseCellKey(anchorKey);
            const to = parseCellKey(key);
            const rowIndexes = [rowKeys.indexOf(from.rowKey), rowKeys.indexOf(to.rowKey)].sort((a, b) => a - b);
            const colIndexes = [editableColumns.indexOf(from.planIdx), editableColumns.indexOf(to.planIdx)].sort((a, b) => a - b);

            if (rowIndexes[0] !== -1 && colIndexes[0] !== -1) {
                const keys = rowKeys.slice(rowIndexes[0], rowIndexes[1] + 1).flatMap(rowKey =>
                    editableColumns.slice(colIndexes[0], colIndexes[1] + 1).map(planIdx => cellKey(rowKey, planIdx))
                );
                setSelection({ quotationUid: quotation.uid, keys });
                setEditingCell(null);
                return;
            }
        }

        if ((event.ctrlKey || event.metaKey) && sameQuotation) {
            const keys = selection.keys.includes(key)
                ? selection.keys.filter(k => k !== key)
                : [...selection.keys, key];
            setSelection(keys.length > 0 ? { quotationUid: quotation.uid, keys } : null);
            setAnchorKey(key);
            setEditingCell(null);
            return;
        }

        setAnchorKey(key);
        setSelection({ quotationUid: quotation.uid, keys: [key] });
        if (!isEditingThisCell) startEdit(quotation, key);
    }

    // Tab moves to the next cell in the row, as in the TOB table; past the table's edge the
    // edit is just committed.
    function moveEdit(rowStep, colStep) {
        if (!editingCell) return;

        const quotation = findQuotation(editingCell.quotationUid);
        if (!quotation) return;
        const { rowKeys, editableColumns } = getQuotationLayout(quotation);
        const { rowKey, planIdx } = parseCellKey(editingCell.key);
        const nextRow = rowKeys[rowKeys.indexOf(rowKey) + rowStep];
        const nextCol = editableColumns[editableColumns.indexOf(planIdx) + colStep];

        if (nextRow === undefined || nextCol === undefined) {
            commitEdit();
            return;
        }

        const nextKey = cellKey(nextRow, nextCol);
        suppressBlurRef.current = true;
        commitEdit();
        setAnchorKey(nextKey);
        setSelection({ quotationUid: quotation.uid, keys: [nextKey] });
        startEdit(quotation, nextKey);
        // Queued after the focus timer startEdit just set, so the flag outlives the blur.
        setTimeout(() => { suppressBlurRef.current = false; }, 0);
    }

    function handleBlur(e) {
        if (suppressBlurRef.current) return;
        if (rateEditorRef.current?.contains(e.relatedTarget)) return;
        commitEdit();
    }

    // Within a rate cell Tab steps from the currency to the amount (and Shift+Tab back)
    // before moving on to the neighbouring cells.
    function handleCurrencyKeyDown(e) {
        if (e.key === 'Tab' && !e.shiftKey) {
            e.preventDefault();
            inputRef.current?.focus();
            return;
        }
        handleKeyDown(e);
    }

    function handleAmountKeyDown(e) {
        if (e.key === 'Tab' && e.shiftKey) {
            e.preventDefault();
            currencyInputRef.current?.focus();
            return;
        }
        handleKeyDown(e);
    }

    function handleKeyDown(e) {
        if (e.key === 'Enter') { e.preventDefault(); commitEdit(); }
        if (e.key === 'Escape') { setEditingCell(null); }
        if (e.key === 'Tab') { e.preventDefault(); moveEdit(0, e.shiftKey ? -1 : 1); }
    }

    function getCellFormatting(quotation, key) {
        const field = rowField(parseCellKey(key).rowKey);
        return getCellPremium(quotation, key)?.cell_styles?.[field] || {};
    }

    // The menu bar shows the state of the first selected cell and applies to all of them.
    const selectionQuotation = selection && findQuotation(selection.quotationUid);
    const selectedCellData = selectionQuotation && selection.keys.length > 0 ? (() => {
        const firstKey = selection.keys[0];
        const formatting = getCellFormatting(selectionQuotation, firstKey);
        return {
            ...formatting,
            font_weight: formatting.font_weight ?? DEFAULT_FONT_WEIGHT[rowField(parseCellKey(firstKey).rowKey)],
        };
    })() : null;

    function formatSelection(patch) {
        if (!selectionQuotation) return;
        onFormatCells?.(selectionQuotation.uid, selection.keys.map(toCellTarget), patch);
    }

    // Props shared by every editable cell: selection outline, click handling and whatever the
    // menu bar has applied. Blank columns stay plain white space.
    function getEditableCell(quotation, rowKey, planIdx, premium) {
        if (!premium || premium.blank) return { isEditing: false, style: {}, onClick: undefined, className: undefined };

        const key = cellKey(rowKey, planIdx);
        const isEditing = editingCell?.quotationUid === quotation.uid && editingCell.key === key;
        const isSelected = selection?.quotationUid === quotation.uid && selection.keys.includes(key);
        const formatting = premium.cell_styles?.[rowField(rowKey)] || {};

        return {
            isEditing,
            // The image-capture path screenshots this table live, so the selection outline is
            // hidden through this class rather than the remove-in-email convention.
            className: isSelected ? 'remove-in-email-selection-outline' : undefined,
            style: {
                cursor: 'text',
                userSelect: 'none',
                ...(formatting.cell_bg_color && { background: formatting.cell_bg_color }),
                ...(formatting.cell_text_color && { color: formatting.cell_text_color }),
                ...(formatting.font_weight && { fontWeight: formatting.font_weight }),
                ...(isSelected && { boxShadow: SELECTED_CELL_OUTLINE }),
            },
            onClick: event => handleCellClick(event, quotation, key, isEditing),
        };
    }

    const editorInputStyle = textAlign => ({
        width: '100%',
        border: 'none',
        outline: 'none',
        backgroundColor: 'transparent',
        // The cell shows through, so the text takes the cell's colour, weight and
        // style -- the header row is white on navy, and inputs reset all three.
        color: 'inherit',
        fontWeight: 'inherit',
        fontStyle: 'inherit',
        padding: 0,
        fontSize: 11,
        fontFamily: 'inherit',
        textAlign,
        boxSizing: 'border-box',
    });

    const renderEditor = () => (
        <input
            ref={inputRef}
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            style={editorInputStyle('inherit')}
        />
    );

    // Laid out like renderAmount, so the cell keeps its shape while being edited.
    const renderRateEditor = () => (
        <table ref={rateEditorRef} width="100%" cellPadding="0" cellSpacing="0" style={{ borderCollapse: 'collapse', fontSize: 'inherit' }}>
            <tbody>
                <tr>
                    <td style={{ textAlign: 'left', width: '40%' }}>
                        <input
                            ref={currencyInputRef}
                            value={editCurrency}
                            placeholder="Currency"
                            onChange={e => setEditCurrency(e.target.value)}
                            onBlur={handleBlur}
                            onKeyDown={handleCurrencyKeyDown}
                            style={editorInputStyle('left')}
                        />
                    </td>
                    <td style={{ textAlign: 'right' }}>
                        <input
                            ref={inputRef}
                            value={editValue}
                            onChange={e => setEditValue(e.target.value)}
                            onBlur={handleBlur}
                            onKeyDown={handleAmountKeyDown}
                            style={editorInputStyle('right')}
                        />
                    </td>
                </tr>
            </tbody>
        </table>
    );

    function handleAddManualPremium(quotation) {
        const planUid = onAddManualPremium?.();
        if (planUid) setPendingManualEdit({ quotationUid: quotation.uid, planUid });
    }

    // Invisible until hovered, then a blue line down the table's right edge with a "+" on it.
    const renderAddColumnZone = (quotation) => (
        <Tooltip title="Add Manual Premium" placement="right">
            <Box
                className="remove-in-email"
                onClick={() => handleAddManualPremium(quotation)}
                sx={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    right: -ADD_COLUMN_ZONE_WIDTH,
                    width: ADD_COLUMN_ZONE_WIDTH,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '& .add-column-line, & .add-column-button': { opacity: 0, transition: 'opacity 150ms ease' },
                    '&:hover .add-column-line, &:hover .add-column-button': { opacity: 1 },
                }}
            >
                <Box className="add-column-line" sx={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: 3, borderRadius: 1, backgroundColor: ADD_COLUMN_COLOR }} />
                <Box
                    className="add-column-button"
                    sx={{
                        position: 'relative',
                        left: -6,
                        width: 20,
                        height: 20,
                        flexShrink: 0,
                        borderRadius: '50%',
                        backgroundColor: ADD_COLUMN_COLOR,
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                    }}
                >
                    <AddIcon sx={{ fontSize: 16 }} />
                </Box>
            </Box>
        </Tooltip>
    );

    // A client or quote added after a plan was quoted leaves that plan stale, with no premium
    // for the new row/table until the plan's dialog is reopened. Manual premiums have nothing
    // to fetch -- the Manual Premium dialog is where they are entered -- so only the hint differs.
    const renderMissingPremium = (premium) => {
        const planKey = premium.plan_uid || premium.plan_id;
        if (!stalePlanKeys?.has(planKey)) return '—';

        const isManual = premium.plan_id === MANUAL_PREMIUM_PLAN_ID;
        return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Tooltip title={isManual ? 'Open the Manual Premium dialog to enter the premium' : 'Open the plan dialog to fetch the premium'}>
                    <PriorityHighIcon
                        className="remove-in-email"
                        onClick={event => {
                            // Opens the dialog rather than starting to type into the cell.
                            event.stopPropagation();
                            onOpenPlan?.(planKey);
                        }}
                        sx={{
                            fontSize: 15,
                            color: MISSING_PREMIUM_COLOR,
                            cursor: 'pointer',
                            borderRadius: '50%',
                            // A red ring pulses out from the icon, so a plan still waiting on a premium is hard to miss.
                            animation: 'missing-premium-pulse 1.4s ease-in-out infinite',
                            '@keyframes missing-premium-pulse': {
                                '0%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(244, 67, 54,0.55)' },
                                '50%': { transform: 'scale(1.2)', boxShadow: '0 0 0 5px rgba(244, 67, 54,0)' },
                                '100%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(244, 67, 54,0)' },
                            },
                        }}
                    />
                </Tooltip>
                —
            </span>
        );
    };

    const renderAmount = (currency, amount) => (
        <table width="100%" cellPadding="0" cellSpacing="0" style={{ borderCollapse: 'collapse', fontSize: 'inherit' }}>
            <tbody>
                <tr>
                    <td style={{ textAlign: 'left' }}>{currency}</td>
                    <td style={{ textAlign: 'right' }}>{Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                </tr>
            </tbody>
        </table>
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }} onMouseEnter={() => setTableHovered(true)} onMouseLeave={() => setTableHovered(false)}>
            <Box className="remove-in-email" sx={{ position: 'sticky', top: 56, zIndex: 2, backgroundColor: '#fff', transition: 'opacity 150ms', ...(!(tableHovered && selectionQuotation && selection.keys.length > 0) && { opacity: 0, visibility: 'hidden', pointerEvents: 'none' }) }}>
                <TOBTableMenuBar
                    cell={selectedCellData}
                    selectedCount={selectionQuotation ? selection.keys.length : 0}
                    onToggleBold={() => formatSelection({ font_weight: isBoldCell(selectedCellData) ? '400' : '700' })}
                    onSetTextColor={color => formatSelection({ cell_text_color: color })}
                    onSetBackgroundColor={color => formatSelection({ cell_bg_color: color })}
                />
            </Box>
            <p className="show-in-email" style={{ textDecoration: 'underline', fontWeight: 'bold', backgroundColor: '#ffeb3b', width: FIRST_COLUMN_WIDTH, padding: 3, paddingLeft: 14, marginTop: 24, display: 'none' }}>{labels.ourIndicativeQuotation}</p>
            {quotations.map((quotation) => {
                const { columnOrder } = getQuotationLayout(quotation);
                const clientPremiums = quotation.client_premiums.map(cp => ({
                    ...cp,
                    premiums: columnOrder.map(i => cp.premiums?.[i]).filter(premium => premium !== undefined),
                }));
                const firstClientPremiums = clientPremiums[0]?.premiums || [];
                const hoveredPremiumIndex = hoveredPremiumIndexByQuotation[quotation.uid] ?? null;
                if (firstClientPremiums.length === 0) return null;

                const planFrequencies = firstClientPremiums.map(p => (p.payment_frequency || '').trim());
                const uniqueFrequencies = new Set(planFrequencies.filter(Boolean).map(f => f.toLowerCase()));
                const allPlansSameFrequency = planFrequencies.every(Boolean) && uniqueFrequencies.size === 1;
                const premiumRowLabel = allPlansSameFrequency
                    ? labels.indicativeFrequencyPremium(normalizeFrequencyLabel(planFrequencies[0], labels))
                    : labels.indicativePremium;

                return (
                    // The PDF moves a quote that doesn't fit to the next page whole, title included,
                    // rather than splitting its rows across two pages.
                    <Box key={quotation.uid} style={{ marginTop: 14, pageBreakInside: 'avoid' }}>
                        <p style={{ fontWeight: 700, fontSize: 16, letterSpacing: 0.3, textDecoration: 'underline' }}>
                            {quotation.name}
                        </p>
                        <Box sx={{ position: 'relative' }}>
                        {onAddManualPremium && renderAddColumnZone(quotation)}
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, tableLayout: 'fixed' }}>
                            <colgroup>
                                <col style={{ width: FIRST_COLUMN_WIDTH }} />
                                {firstClientPremiums.map((_, i) => <col key={i} />)}
                            </colgroup>
                            <thead>
                                <tr className="remove-in-email">
                                    <td style={{ padding: CELL_PAD, border: 'none' }} />
                                    {firstClientPremiums.map((premium, i) => (
                                        <td
                                            key={i}
                                            style={{ padding: CELL_PAD, border: 'none', textAlign: 'center' }}
                                            onMouseEnter={() => setHoveredPremiumIndexByQuotation(prev => ({ ...prev, [quotation.uid]: i }))}
                                            onMouseLeave={() => setHoveredPremiumIndexByQuotation(prev => {
                                                const next = { ...prev };
                                                delete next[quotation.uid];
                                                return next;
                                            })}
                                        >
                                            {!premium.blank && <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={!!premium.recommended}
                                                        onChange={e => onUpdateRecommended?.(quotation.uid, columnOrder[i], e.target.checked)}
                                                        size="small"
                                                        sx={{ p: 0, color: '#1a3055' }}
                                                    />
                                                }
                                                label="Recommended"
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: 1,
                                                    margin: 0,
                                                    fontSize: 11,
                                                    opacity: hoveredPremiumIndex === i ? 1 : 0,
                                                    transition: 'opacity 150ms ease',
                                                    pointerEvents: hoveredPremiumIndex === i ? 'auto' : 'none',
                                                    '& .MuiFormControlLabel-label': {
                                                        fontSize: 11,
                                                    },
                                                }}
                                            />}
                                        </td>
                                    ))}
                                </tr>
                                {/* Top header: Premium Comparison label + insurer names */}
                                <tr>
                                    <th style={{ background: HEADER_BG, color: HEADER_COLOR, padding: CELL_PAD, border: CELL_BORDER, textAlign: 'left', fontWeight: 700, textTransform: 'uppercase', fontSize: 11 }}>
                                        {labels.premiumComparison}
                                    </th>
                                    {firstClientPremiums.map((premium, i) => {
                                        const cell = getEditableCell(quotation, 'insurer', columnOrder[i], premium);
                                        return (
                                            <th
                                                key={i}
                                                className={joinClasses(premium.blank && 'remove-border-in-email', cell.className)}
                                                style={{ background: HEADER_BG, color: HEADER_COLOR, padding: CELL_PAD, border: CELL_BORDER, textAlign: 'center', fontWeight: 700, textTransform: 'uppercase', fontSize: 11, ...cell.style, ...(premium.blank && BLANK_CELL_STYLE) }}
                                                onClick={cell.onClick}
                                                onMouseEnter={() => setHoveredPremiumIndexByQuotation(prev => ({ ...prev, [quotation.uid]: i }))}
                                                onMouseLeave={() => setHoveredPremiumIndexByQuotation(prev => {
                                                    const next = { ...prev };
                                                    delete next[quotation.uid];
                                                    return next;
                                                })}
                                            >
                                                {cell.isEditing ? renderEditor() : premium.blank ? '' : (normalizeInsurerName(premium.insurer) || '—')}
                                            </th>
                                        );
                                    })}
                                </tr>
                                {/* Plan Type label (rowspan=2): row 1 = plan name */}
                                <tr>
                                    <td rowSpan="2" style={{ padding: CELL_PAD, border: CELL_BORDER, borderBottom: 'none', fontWeight: 700, fontSize: 11, verticalAlign: 'middle' }}>{labels.planType}</td>
                                    {firstClientPremiums.map((premium, i) => {
                                        const cell = getEditableCell(quotation, 'plan_label', columnOrder[i], premium);
                                        return (
                                            <td
                                                key={i}
                                                className={joinClasses('remove-border-bottom-on-email', premium.blank && 'remove-border-in-email', cell.className)}
                                                style={{ padding: CELL_PAD, border: CELL_BORDER, borderBottom: 'none', paddingBottom: 0, textAlign: 'center', fontSize: 11, fontWeight: 700, ...cell.style, ...(premium.blank && BLANK_CELL_STYLE) }}
                                                onClick={cell.onClick}
                                            >
                                                {cell.isEditing ? renderEditor() : premium.blank ? '' : (premium.plan_label || '—')}
                                            </td>
                                        );
                                    })}
                                </tr>
                                {/* Plan Type label (rowspan=2): row 2 = included_benefits */}
                                <tr>
                                    {firstClientPremiums.map((premium, i) => {
                                        const cell = getEditableCell(quotation, 'included_benefits', columnOrder[i], premium);
                                        return (
                                            <td
                                                key={i}
                                                className={joinClasses('remove-border-top-on-email', premium.blank && 'remove-border-in-email', cell.className)}
                                                style={{ padding: CELL_PAD, border: CELL_BORDER, borderTop: 'none', paddingTop: 0, textAlign: 'center', fontStyle: 'italic', fontSize: 11, minWidth: 80, ...cell.style, ...(premium.blank && BLANK_CELL_STYLE) }}
                                                onClick={cell.onClick}
                                            >
                                                {cell.isEditing ? renderEditor() : premium.blank ? null : (
                                                    premium.included_benefits || <span className='remove-in-email' style={{ color: '#bbb' }}>Click to edit</span>
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {/* Deductible */}
                                <tr>
                                    <td style={{ padding: CELL_PAD, border: CELL_BORDER, fontWeight: 700, fontSize: 11 }}>{labels.deductible}</td>
                                    {firstClientPremiums.map((premium, i) => {
                                        const cell = getEditableCell(quotation, 'deductible', columnOrder[i], premium);
                                        return (
                                            <td
                                                key={i}
                                                className={joinClasses(premium.blank && 'remove-border-in-email', cell.className)}
                                                style={{ padding: CELL_PAD, border: CELL_BORDER, textAlign: 'center', fontWeight: 700, fontSize: 11, ...cell.style, ...(premium.blank && BLANK_CELL_STYLE) }}
                                                onClick={cell.onClick}
                                            >
                                                {cell.isEditing ? renderEditor() : !premium.blank && <>
                                                    {normalizeDeductibleDisplay(premium.deductible, language) || '—'}
                                                    {premium.optional_covers?.includes('op') && premium.co_insurance ? ` | ${translateDeductiblePart(`Co-ins: ${premium.co_insurance}`, language)}` : ''}
                                                </>}
                                            </td>
                                        );
                                    })}
                                </tr>
                                {/* Area of cover */}
                                <tr>
                                    <td style={{ padding: CELL_PAD, border: CELL_BORDER, fontWeight: 700, fontSize: 11 }}>{labels.areaOfCover}</td>
                                    {firstClientPremiums.map((premium, i) => {
                                        const cell = getEditableCell(quotation, 'area_of_coverage', columnOrder[i], premium);
                                        return (
                                            <td
                                                key={i}
                                                className={joinClasses(premium.blank && 'remove-border-in-email', cell.className)}
                                                style={{ padding: CELL_PAD, border: CELL_BORDER, textAlign: 'center', fontSize: 11, fontWeight: 700, ...cell.style, ...(premium.blank && BLANK_CELL_STYLE) }}
                                                onClick={cell.onClick}
                                            >
                                                {cell.isEditing ? renderEditor() : premium.blank ? '' : (normalizeAreaOfCoverDisplay(premium.area_of_coverage, language) || '—')}
                                            </td>
                                        );
                                    })}
                                </tr>
                                {/* Per-client premium rows */}
                                {clientPremiums.map((cp) => {
                                    const client = clients.find(c => c.uid === cp.client_uid);
                                    const clientLabel = client
                                        ? [client.full_name || 'Client', client.age || '', client.country_of_residence || ''].filter(Boolean).join(', ')
                                        : 'Client';
                                    return (
                                        <tr key={cp.client_uid}>
                                            <td style={{ padding: CELL_PAD, border: CELL_BORDER, fontSize: 11 }}>{clientLabel}</td>
                                            {cp.premiums.map((premium, i) => {
                                                const cell = getEditableCell(quotation, RATE_ROW_PREFIX + cp.client_uid, columnOrder[i], premium);
                                                return (
                                                    <td
                                                        key={i}
                                                        className={joinClasses(premium.blank && 'remove-border-in-email', cell.className)}
                                                        style={{ padding: CELL_PAD, border: CELL_BORDER, fontSize: 11, ...cell.style, ...(premium.blank && BLANK_CELL_STYLE) }}
                                                        onClick={cell.onClick}
                                                    >
                                                        {cell.isEditing ? renderRateEditor()
                                                            : premium.premium_rate != null ? renderAmount(premium.currency, premium.premium_rate)
                                                            : premium.blank ? '' : renderMissingPremium(premium)}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                                {/* Tax row - only shown if at least one plan has tax
                                {firstClientPremiums.some(p => p.total_tax != null) && (
                                    <tr>
                                        <td style={{ padding: CELL_PAD, border: CELL_BORDER, fontWeight: 700, fontSize: 11 }}>TAX</td>
                                        {firstClientPremiums.map((premium, i) => {
                                            const tax = premium.total_tax;
                                            const currency = premium.currency || '';
                                            return (
                                                <td key={i} style={{ padding: CELL_PAD, border: CELL_BORDER, fontSize: 11 }}>
                                                    {tax != null ? renderAmount(currency, tax) : '—'}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                )}
                                */}
                                {/* Indicative annual premium total -- always the sum of the rows above, so not editable */}
                                <tr>
                                    <td style={{ padding: CELL_PAD, border: CELL_BORDER, fontWeight: 700, fontSize: 11, textTransform: 'uppercase' }}>
                                        {premiumRowLabel}
                                    </td>
                                    {firstClientPremiums.map((premium, planIdx) => {
                                        const currency = premium.currency || '';
                                        const hasData = clientPremiums.some(cp => cp.premiums[planIdx]?.premium_rate != null);
                                        const total = clientPremiums.reduce((sum, cp) => {
                                            const rate = cp.premiums[planIdx]?.premium_rate;
                                            return rate != null ? sum + Number(rate) : sum;
                                        }, 0);
                                        const currencyLabel = !allPlansSameFrequency && planFrequencies[planIdx]
                                            ? `${currency} / ${normalizeFrequencyLabel(planFrequencies[planIdx], labels)}`
                                            : currency;
                                        return (
                                            <td key={planIdx} className={premium.blank ? 'remove-border-in-email' : undefined} style={{ padding: CELL_PAD, border: CELL_BORDER, fontWeight: 700, fontSize: 11, ...(premium.blank && BLANK_CELL_STYLE) }}>
                                                {hasData ? renderAmount(currencyLabel, total) : premium.blank ? '' : '—'}
                                            </td>
                                        );
                                    })}
                                </tr>
                                <tr className="remove-border-in-email">
                                    <td style={{ padding: CELL_PAD, border: 'none', fontWeight: 700, fontSize: 11, textTransform: 'uppercase' }}>
                                       &nbsp;
                                    </td>
                                    {firstClientPremiums.map((premium, i) => (
                                        <td key={i} style={{ padding: CELL_PAD, border: 'none', textAlign: 'center', fontSize: 11 }}>
                                            {premium.recommended && !premium.blank ? (
                                                <span style={{ color: '#e57373', textDecoration: 'underline', fontWeight: 700, textTransform: 'uppercase' }}>
                                                    {labels.recommended}
                                                </span>
                                            ) : null}
                                        </td>
                                    ))}
                                </tr>
                            </tbody>
                        </table>
                        </Box>
                    </Box>
                );
            })}
            <p className="show-in-email" style={{ fontSize: getFirstClientPremiumsLength() <= MAX_PLANS_FOR_LARGE_FONT ? LARGE_FONT_SIZE : REGULAR_FONT_SIZE, fontStyle: 'italic', color: '#f44336', display: 'none', marginTop: 14 }}>{labels.disclaimer}</p>
        </Box>
    );
}
